import React, { useState, useEffect } from 'react';
import { useLogto } from '@logto/react';
import { Settings, Bot, Check, Circle, Lightbulb, X } from 'lucide-react';
import { settingsService, OpenAIStatus } from '../services/settingsService';

interface SettingsModalProps {
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ onClose }) => {
  const [apiKey, setApiKey] = useState('');
  const [openaiStatus, setOpenaiStatus] = useState<OpenAIStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const logto = useLogto();

  useEffect(() => {
    checkOpenAIStatus();
  }, []);

  const checkOpenAIStatus = async () => {
    setLoading(true);
    const status = await settingsService.getOpenAIStatus(logto);
    setOpenaiStatus(status);
    setLoading(false);
  };

  const handleSaveApiKey = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKey.trim()) {
      setMessage({ type: 'error', text: 'API key cannot be empty' });
      return;
    }

    setSaving(true);
    const result = await settingsService.setOpenAIKey(logto, apiKey);
    setSaving(false);

    if (result.success) {
      setMessage({ type: 'success', text: 'OpenAI API key saved successfully!' });
      setApiKey('');
      await checkOpenAIStatus();
      setTimeout(() => setMessage(null), 3000);
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleDeleteApiKey = async () => {
    if (!window.confirm('Are you sure you want to remove your OpenAI API key?')) {
      return;
    }

    setSaving(true);
    const result = await settingsService.deleteOpenAIKey(logto);
    setSaving(false);

    if (result.success) {
      setMessage({ type: 'success', text: 'OpenAI API key removed' });
      setApiKey('');
      await checkOpenAIStatus();
      setTimeout(() => setMessage(null), 3000);
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" /> Settings
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-purple-800 rounded-full w-8 h-8 flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* OpenAI Settings Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" /> OpenAI ChatGPT
            </h3>

            {loading ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                <p className="text-gray-600 mt-2 text-sm">Loading settings...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Status Display */}
                {openaiStatus && (
                  <div
                    className={`p-3 rounded-lg border ${openaiStatus.configured
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-amber-50 border-amber-200 text-amber-700'
                      }`}
                  >
                    <p className="font-medium flex items-center gap-1">
                      {openaiStatus.configured ? (
                        <><Check className="w-4 h-4" /> Connected</>
                      ) : (
                        <><Circle className="w-4 h-4" /> Not Connected</>
                      )}
                    </p>
                    <p className="text-sm mt-1">{openaiStatus.message}</p>
                    {openaiStatus.configured && openaiStatus.masked_key && (
                      <p className="text-xs mt-2 font-mono">Key: {openaiStatus.masked_key}</p>
                    )}
                  </div>
                )}

                {/* API Key Input Form */}
                <form onSubmit={handleSaveApiKey} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OpenAI API Key
                    </label>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="sk-..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      disabled={saving}
                    />
                    <p className="text-xs text-gray-600 mt-2">
                      Get your API key from{' '}
                      <a
                        href="https://platform.openai.com/api-keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:underline"
                      >
                        OpenAI Platform
                      </a>
                    </p>
                  </div>

                  {/* Message Display */}
                  {message && (
                    <div
                      className={`p-3 rounded-lg text-sm ${message.type === 'success'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                    >
                      {message.text}
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      disabled={saving || !apiKey.trim()}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {saving ? 'Saving...' : 'Save Key'}
                    </button>

                    {openaiStatus?.configured && (
                      <button
                        type="button"
                        onClick={handleDeleteApiKey}
                        disabled={saving}
                        className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        {saving ? 'Removing...' : 'Remove'}
                      </button>
                    )}
                  </div>
                </form>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                  <p className="font-semibold mb-1 flex items-center gap-1">
                    <Lightbulb className="w-4 h-4" /> About AI Chat
                  </p>
                  <p>
                    Once you set your OpenAI API key, you'll be able to chat with our AI assistant to
                    discuss model results and compare different models. Your key is stored securely on
                    our server.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
